import { useState, useEffect, FormEvent, useRef } from "react";
import { v4 as uuid } from "uuid";
import { AxiosProgressEvent } from "axios";
import {
  STATIC_BUSINESS_AREAS,
  STATIC_ASSET_TYPES,
  OTHER_AGENCY_ID,
} from "./brandComplianceStaticData";
import { getClientEnvironment } from "utils";
// Using Formik here instead of the FormikProps interface because of adding setValues - minimal impact.
import { FormikProps } from "formik";
import {
  Input,
  Button,
  ButtonGroup,
  TextArea,
  Checkbox,
  FlexBox,
  Body,
  MdToHtml,
  Loader,
  Select,
  Title,
  DragAndDrop,
  TwoUp,
  Tooltip,
} from "components";
import {
  useGetBrandComplianceConfigQuery,
  useSubmitBrandComplianceMutation,
  setNotification,
} from "services";
import { useDispatch } from "hooks/redux";
import { useS3BrandComplianceUpload } from "hooks";
import {
  BrandComplianceFormValues,
  BrandComplianceSubmitRequest,
  BrandComplianceUploadedFileProps,
} from "interfaces/BrandCompliance";
import BrandComplianceFileRow from "./BrandComplianceFileRow";
import {
  StyledForm,
  StyledSection,
  StyledRadioGroup,
  DragWrapper,
  SuccessPanel,
  ChecklistWrapper,
  BlackLinkWrapper,
  UploadHelpGroup,
} from "./BrandComplianceForm.module";

const YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

const BrandComplianceForm = ({
  values: formValues,
  touched,
  errors,
  handleChange,
  handleBlur,
  resetForm,
  setFieldValue,
  setFieldTouched,
  setValues,
  dirty,
  isValid,
}: FormikProps<BrandComplianceFormValues>) => {
  const {
    name,
    email,
    agencyId,
    otherCreativeAgency,
    isResubmission,
    referenceNumber,
    publicationType,
    submissionTitle,
    submissionSummary,
    hasExceptions,
    exceptionsSummary,
    hasTechnicalLimitations,
    technicalLimitationsSummary,
    hasCoBranding,
    hasLegalAgreement,
    isBrandIdentity,
    hasAiImagery,
    businessAreaId,
    functionalAreaId,
    verizonClientName,
    verizonDirectorName,
    verizonVpName,
    files,
    fileAssetTypes,
  }: BrandComplianceFormValues = formValues;

  const dispatch = useDispatch();

  // FE-7: stubbed config query — defaults to empty arrays if endpoint not yet available
  const { data: config } = useGetBrandComplianceConfigQuery(undefined, {
    // If the endpoint 404s, suppress the global error notification and just use empty arrays
  });
  const agencies = config?.agencies ?? [];
  const businessAreas = config?.businessAreas?.length ? config.businessAreas : STATIC_BUSINESS_AREAS;
  const functionalAreas = config?.functionalAreas ?? [];
  const assetTypes = (config?.assetTypes?.length ? config.assetTypes : STATIC_ASSET_TYPES).map(
    (n: string) => ({ id: n, name: n })
  );

  const [submitBrandCompliance, { isLoading: submitLoading }] = useSubmitBrandComplianceMutation();
  const { uploadS3, loading: uploadLoading } = useS3BrandComplianceUpload();

  const [packageId, setPackageId] = useState<string>("");
  const [workfrontReferenceNumber, setWorkfrontReferenceNumber] = useState<string>("");
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  useEffect(() => {
    setPackageId(uuid());
  }, []);

  const fileStateRef = useRef<File[]>();
  fileStateRef.current = files;

  // deleteFileFromForm/handleUploadFailure run from async upload callbacks
  // registered on earlier renders, so they must read the latest asset-type
  // map through a ref rather than closing over the (possibly stale) prop.
  const fileAssetTypesRef = useRef<Record<string, string>>();
  fileAssetTypesRef.current = fileAssetTypes;

  const [uploadingFiles, _setUploadingFiles] = useState<string[]>([]);

  // Full S3 object key per uploaded file (keyed by file name). Each policy fetch
  // generates a new GUID folder, so the actual key must be captured at upload time.
  const [uploadedFileKeys, _setUploadedFileKeys] = useState<Record<string, string>>({});

  // Upload progress per file (keyed by file name). The file rows render outside
  // DragAndDrop (per design), so the form tracks progress itself instead of
  // using DragAndDrop's internal progress callback.
  const [uploadProgress, _setUploadProgress] = useState<Record<string, AxiosProgressEvent>>({});

  const handleUploadProgress = (fileName: string, progressEvent: AxiosProgressEvent) => {
    _setUploadProgress((old: Record<string, AxiosProgressEvent>) => ({
      ...old,
      [fileName]: progressEvent,
    }));
  };

  const handleSuccess = (response: BrandComplianceUploadedFileProps) => {
    _setUploadedFileKeys((oldKeys: Record<string, string>) => ({
      ...oldKeys,
      [response.name]: response.fileKey,
    }));
    _setUploadingFiles((oldArr: string[]) =>
      oldArr.filter((item: string) => item !== response.name)
    );
  };

  const deleteFileFromForm = (file: File) => {
    setValues(
      (currentValues) => {
        const nextFiles = currentValues.files.filter(
          (item: File) => item.name !== file.name
        );

        // File names may contain dots (e.g. "Screenshot 2026-06-25 at 3.59.39PM.png"),
        // which Formik would otherwise treat as a nested path — always set the
        // whole fileAssetTypes object rather than a dotted field path.
        const {
          [file.name]: _removedAssetType,
          ...remainingAssetTypes
        } = currentValues.fileAssetTypes;

        return {
          ...currentValues,
          files: nextFiles,
          fileAssetTypes: remainingAssetTypes,
        };
      },
      true
    );

    _setUploadedFileKeys((oldKeys: Record<string, string>) => {
      const { [file.name]: _removed, ...rest } = oldKeys;
      return rest;
    });

    _setUploadProgress((old: Record<string, AxiosProgressEvent>) => {
      const { [file.name]: _removedProgress, ...rest } = old;
      return rest;
    });
  };

  const handleAssetTypeChange = (file: File, value: string) => {
    const nextFileAssetTypes = {
      ...fileAssetTypes,
      [file.name]: value,
    };

    // Workaround for Formik validation race condition.
    // Validate after updating the value, not the previous value.
    setFieldTouched("fileAssetTypes", true, false);
    setFieldValue("fileAssetTypes", nextFileAssetTypes, true);
  };

  const handleUploadFailure = (file: File) => {
    // Upload never landed in S3 — drop the file from the form so a keyless
    // file can't be submitted.
    _setUploadingFiles((oldArr: string[]) =>
      oldArr.filter((item: string) => item !== file.name)
    );
    deleteFileFromForm(file);
  };

  const handleFilesChange = (newFiles: File[]) => {
    setFieldValue(
      "files",
      fileStateRef.current ? [...fileStateRef.current, ...newFiles] : newFiles
    );
    newFiles.forEach((file: File) => {
      _setUploadingFiles((oldArr: string[]) => [...oldArr, file.name]);
      uploadS3(file, handleSuccess, packageId, handleUploadProgress, handleUploadFailure);
    });
  };

  const handleRemoveFile = (file: File) => {
    // Brand-compliance files are stored indefinitely and have no delete endpoint;
    // removing a file only takes it out of local form state (orphaned S3 object
    // is acceptable by design).
    deleteFileFromForm(file);
  };

  const handleRadioChange = (fieldName: string) => (i: number) => () => {
    // VDS RadioButton's onChange passes the event, not the value. RadioGroup gives
    // us the option index, so resolve the selected value from YES_NO_OPTIONS.
    const value = YES_NO_OPTIONS[i].value;
    setFieldValue(fieldName, value);
  };

  const handlePublicationTypeChange = (type: string) => {
    const current = publicationType as string[];
    if (current.includes(type)) {
      setFieldValue("publicationType", current.filter((t) => t !== type));
    } else {
      setFieldValue("publicationType", [...current, type]);
    }
  };

  const handleOnSubmit = (_e: FormEvent) => {
    if (!dirty || !isValid) {
      // Surface per-row "select an asset type" errors even if the user
      // never touched a row's Select directly.
      setFieldTouched("fileAssetTypes", true);
      // FE-9: validation failure notification
      dispatch(
        setNotification({
          type: "error",
          message: "Please complete all required fields before submitting.",
        })
      );
      return;
    }

    const payload: BrandComplianceSubmitRequest = {
      packageId,
      fullName: name,
      email,
      agencyId,
      otherCreativeAgency: agencyId === OTHER_AGENCY_ID ? otherCreativeAgency : undefined,
      isResubmission: isResubmission === "Yes",
      resubmissionReferenceNumber: isResubmission === "Yes" ? referenceNumber : undefined,
      printDigital: publicationType as string[],
      submissionTitle,
      submissionSummary,
      hasExceptions: hasExceptions === "Yes",
      exceptionsSummary: hasExceptions === "Yes" ? exceptionsSummary : undefined,
      hasTechnicalLimitations: hasTechnicalLimitations === "Yes",
      technicalLimitationsSummary:
        hasTechnicalLimitations === "Yes" ? technicalLimitationsSummary : undefined,
      hasCoBranding: hasCoBranding === "Yes",
      hasLegalAgreement: hasCoBranding === "Yes" ? hasLegalAgreement === "Yes" : undefined,
      isBrandIdentity: hasCoBranding === "Yes" ? isBrandIdentity === "Yes" : undefined,
      hasAiImagery: hasAiImagery === "Yes",
      businessAreaId,
      functionalAreaId,
      verizonClientName,
      verizonDirectorName,
      verizonVpName,
      // Full S3 object keys captured at upload time — the backend reads
      // submission attachments from the BrandComplianceBucket by these keys.
      files: files
        .map((f: File) => ({
          fileKey: uploadedFileKeys[f.name],
          fileName: f.name,
          assetType: fileAssetTypes[f.name],
        }))
        .filter((x) => !!x.fileKey),
      // Browser/OS metadata recorded on the Workfront issue
      ...getClientEnvironment(),
    };

    // FE-8: submit + success; FE-9: recoverable submit/Workfront error
    submitBrandCompliance(payload)
      .unwrap()
      .then((response) => {
        setWorkfrontReferenceNumber(response.workfrontReferenceNumber);
        setShowSuccess(true);
      })
      .catch(() => {
        dispatch(
          setNotification({
            type: "error",
            message:
              "Could not submit your Brand Compliance request. Please try again.",
          })
        );
      });
  };

  const handleReset = () => {
    resetForm();
    setShowSuccess(false);
    setWorkfrontReferenceNumber("");
    setPackageId(uuid());
    _setUploadingFiles([]);
    _setUploadedFileKeys({});
    _setUploadProgress({});
  };

  if (showSuccess) {
    return (
      <SuccessPanel gap="1rem">
        <Title size="large" bold>
          Your submission has been added to the queue!
        </Title>
        <Body size="large">
          An email confirmation containing your reference number has been
          sent to your inbox. Please retain for your records. Feedback will
          be sent within 3 business days unless you hear from us otherwise.
        </Body>
        <Body size="large" bold>
          Reference number: {workfrontReferenceNumber}
        </Body>
        <ButtonGroup align="flex-start" pd="1rem 0 0">
          <Button use="primary" type="button" onClick={handleReset}>
            Submit another
          </Button>
        </ButtonGroup>
      </SuccessPanel>
    );
  }

  return (
    <>
      <StyledForm data-testid="brand-compliance-form" onSubmit={handleOnSubmit}>

        {/* ─── About you ────────────────────────────────────────────── */}
        <StyledSection gap="1rem">
          <Title size="large" bold>
            About you
          </Title>

          <TwoUp pd="0">
            <Input
              label="Full Name"
              name="name"
              value={name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={(touched.name && !!errors.name) || false}
              errorText={errors.name}
              required
              data-testid="name"
            />

            <Input
              label="Email"
              name="email"
              value={email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={(touched.email && !!errors.email) || false}
              errorText={errors.email}
              required
              data-testid="email"
            />
          </TwoUp>

          <TwoUp pd="0">
            <Select
              label="Name of creative agency who designed this work"
              tooltipTitle="Verizon Employees:"
              tooltipContent='If you’re working with an agency or vendor, they should submit the work directly so the submission can be attributed to them. If your agency is not listed, select "Other" and enter its name.'
              name="agencyId"
              value={agencyId}
              items={agencies}
              optionLabels={["name"]}
              valueKey="id"
              placeholderLabel="Agency name"
              onChange={handleChange}
              error={(touched.agencyId && !!errors.agencyId) || false}
              errorText={errors.agencyId}
              data-testid="agency"
            />
            {/* Conditional: free-text agency name (agency = Other) */}
            {agencyId === OTHER_AGENCY_ID ? (
              <Input
                label="Other creative agency"
                name="otherCreativeAgency"
                value={otherCreativeAgency}
                onChange={handleChange}
                onBlur={handleBlur}
                error={(touched.otherCreativeAgency && !!errors.otherCreativeAgency) || false}
                errorText={errors.otherCreativeAgency}
                required
                data-testid="other-creative-agency"
              />
            ) : (
              <div />
            )}
          </TwoUp>
        </StyledSection>

        {/* ─── About your submission ───────────────────────────────── */}
        <StyledSection gap="1.25rem">
          <Title size="large" bold>
            About your submission
          </Title>

          {/* Row: resubmission (+ conditional reference number) | print/digital */}
          <TwoUp pd="0">
            <FlexBox gap="1rem">
              <StyledRadioGroup
                title={<Body size="small" bold color="#6f7171" pd="0 0 0.625rem 0">Is this a resubmission?</Body>}
                name="isResubmission"
                options={YES_NO_OPTIONS}
                value={isResubmission}
                onChange={handleRadioChange("isResubmission")}
                error={(touched.isResubmission && !!errors.isResubmission) || false}
                inline
              />

              {/* Conditional: Reference number (resubmission = Yes) */}
              {isResubmission === "Yes" && (
                // TODO: confirm copy with PM — label and validation for reference number conditional field
                <Input
                  label="Reference number"
                  name="referenceNumber"
                  value={referenceNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={(touched.referenceNumber && !!errors.referenceNumber) || false}
                  errorText={errors.referenceNumber}
                  required
                  data-testid="reference-number"
                  maxLength={7}
                />
              )}
            </FlexBox>

            {/* Print / Digital checkboxes */}
            <FlexBox gap="0.5rem">
              <Body size="small" bold color="#6f7171">
                Is this creative asset for print and/or digital publication?
              </Body>
              <FlexBox row gap="1.5rem">
                <FlexBox row gap="0.5rem" align="center">
                  <Checkbox
                    width="1.5rem"
                    required={false}
                    selected={(publicationType as string[]).includes("Digital")}
                    onChange={() => handlePublicationTypeChange("Digital")}
                  />
                  <Body size="small" bold color="#6f7171">Digital</Body>
                </FlexBox>
                <FlexBox row gap="0.5rem" align="center">
                  <Checkbox
                    width="1.5rem"
                    required={false}
                    selected={(publicationType as string[]).includes("Print")}
                    onChange={() => handlePublicationTypeChange("Print")}
                  />
                  <Body size="small" bold color="#6f7171">Print</Body>
                </FlexBox>
              </FlexBox>
              {touched.publicationType && !!errors.publicationType && (
                <Body size="small" color="#ee0000">
                  {errors.publicationType as string}
                </Body>
              )}
            </FlexBox>
          </TwoUp>

          {/* Row: submission title | submission summary */}
          <TwoUp pd="0">
            <FlexBox gap="0.5rem">
              <Body size="large">What is the name of your submission?</Body>
              <Input
                label="Submission title"
                name="submissionTitle"
                value={submissionTitle}
                onChange={handleChange}
                onBlur={handleBlur}
                error={(touched.submissionTitle && !!errors.submissionTitle) || false}
                errorText={errors.submissionTitle}
                required
                data-testid="submission-title"
              />
            </FlexBox>

            <FlexBox gap="0.5rem">
              <Body size="large">
                Please provide a detailed summary of the creative asset you&apos;re submitting. (e.g. relevant background, audience, launch date, whether it is for a campaign, toolkits referenced, URLs if submitting flat files or web pages)
              </Body>
              <TextArea
                label="Submission summary"
                name="submissionSummary"
                value={submissionSummary}
                onChange={handleChange}
                onBlur={handleBlur}
                error={(touched.submissionSummary && !!errors.submissionSummary) || false}
                errorText={errors.submissionSummary}
                required
                data-testid="submission-summary"
                maxLength={1500}
              />
            </FlexBox>
          </TwoUp>

          {/* Row: exceptions | technical limitations (each with its conditional summary) */}
          <TwoUp pd="0">
            <FlexBox gap="1rem">
              <StyledRadioGroup
                title={
                  <Body size="small" bold color="#6f7171" pd="0 0 0.625rem 0">
                    Does your creative feature exceptions and/or leadership permission to break from our brand guidelines? (e.g. campaign-specific guidelines)
                  </Body>
                }
                name="hasExceptions"
                options={YES_NO_OPTIONS}
                value={hasExceptions}
                onChange={handleRadioChange("hasExceptions")}
                error={(touched.hasExceptions && !!errors.hasExceptions) || false}
                inline
              />

              {/* Conditional: Exceptions summary (hasExceptions = Yes) */}
              {hasExceptions === "Yes" && (
                // TODO: confirm copy with PM — label and validation for exceptions summary conditional field
                <TextArea
                  label="Please describe the exceptions / permissions"
                  name="exceptionsSummary"
                  value={exceptionsSummary}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={(touched.exceptionsSummary && !!errors.exceptionsSummary) || false}
                  errorText={errors.exceptionsSummary}
                  required
                  data-testid="exceptions-summary"
                />
              )}
            </FlexBox>

            <FlexBox gap="1rem">
              <StyledRadioGroup
                title={
                  <Body size="small" bold color="#6f7171" pd="0 0 0.625rem 0">
                    Are there technical limitations that prevent you from adhering to our brand guidelines? (e.g. you're using design software that doesn't have the Verizon brand typeface)
                  </Body>
                }
                name="hasTechnicalLimitations"
                options={YES_NO_OPTIONS}
                value={hasTechnicalLimitations}
                onChange={handleRadioChange("hasTechnicalLimitations")}
                error={(touched.hasTechnicalLimitations && !!errors.hasTechnicalLimitations) || false}
                inline
              />

              {/* Conditional: Technical limitations summary (hasTechnicalLimitations = Yes) */}
              {hasTechnicalLimitations === "Yes" && (
                // TODO: confirm copy with PM — label and validation for technical limitations summary conditional field
                <TextArea
                  label="Please describe the technical limitations"
                  name="technicalLimitationsSummary"
                  value={technicalLimitationsSummary}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={(touched.technicalLimitationsSummary && !!errors.technicalLimitationsSummary) || false}
                  errorText={errors.technicalLimitationsSummary}
                  required
                  data-testid="technical-limitations-summary"
                />
              )}
            </FlexBox>
          </TwoUp>

          {/* Row: co-branding (+ conditionals) | AI imagery */}
          <TwoUp pd="0">
            <FlexBox gap="1rem">
              <StyledRadioGroup
                title={
                  <Body size="small" bold color="#6f7171" pd="0 0 0.625rem 0">
                    Does this creative feature co-branding or alliance branding? (i.e. Verizon's logo featured alongside 3rd party logos or identity elements)
                  </Body>
                }
                name="hasCoBranding"
                options={YES_NO_OPTIONS}
                value={hasCoBranding}
                onChange={handleRadioChange("hasCoBranding")}
                error={(touched.hasCoBranding && !!errors.hasCoBranding) || false}
                inline
              />

              {/* Conditional: Legal agreement + brand identity (hasCoBranding = Yes) */}
              {hasCoBranding === "Yes" && (
                <>
                  {/* TODO: confirm copy with PM — label and validation for legal agreement conditional field */}
                  <StyledRadioGroup
                    title={
                      <FlexBox row align="center" gap="0.25rem" pd="0 0 0.625rem 0">
                        <Body size="small" bold color="#6f7171">Is there a legal agreement in place?</Body>
                        <Tooltip
                          size="small"
                          ariaLabel="Is there a legal agreement in place? tooltip"
                        >
                          All work with co-branding, multiple logos or a designation line must have a legal agreement in place that permits the partner to use the Verizon logo.
                        </Tooltip>
                      </FlexBox>
                    }
                    name="hasLegalAgreement"
                    options={YES_NO_OPTIONS}
                    value={hasLegalAgreement}
                    onChange={handleRadioChange("hasLegalAgreement")}
                    error={(touched.hasLegalAgreement && !!errors.hasLegalAgreement) || false}
                    inline
                  />
                  {/* TODO: confirm copy with PM — label and validation for brand identity conditional field */}
                  <StyledRadioGroup
                    title={
                      <FlexBox row align="center" gap="0.25rem" pd="0 0 0.625rem 0">
                        <Body size="small" bold color="#6f7171">Is this creative in Verizon's brand identity?</Body>
                        <Tooltip
                          size="small"
                          ariaLabel="Is this creative in Verizon's brand identity? tooltip"
                        >
                          Please indicate if this creative follows Verizon’s look and feel or the partner’s. It should not be a blend of the two.
                        </Tooltip>
                      </FlexBox>
                    }
                    name="isBrandIdentity"
                    options={YES_NO_OPTIONS}
                    value={isBrandIdentity}
                    onChange={handleRadioChange("isBrandIdentity")}
                    error={(touched.isBrandIdentity && !!errors.isBrandIdentity) || false}
                    inline
                  />
                </>
              )}
            </FlexBox>

            <StyledRadioGroup
              title={<Body size="small" bold color="#6f7171" pd="0 0 0.625rem 0">Does this creative feature AI-generated photography or imagery?</Body>}
              name="hasAiImagery"
              options={YES_NO_OPTIONS}
              value={hasAiImagery}
              onChange={handleRadioChange("hasAiImagery")}
              error={(touched.hasAiImagery && !!errors.hasAiImagery) || false}
              inline
            />
          </TwoUp>

          {/* Row: business area | functional area */}
          <TwoUp pd="0">
            <Select
              label="Verizon business area"
              name="businessAreaId"
              value={businessAreaId}
              items={businessAreas}
              optionLabels={["name"]}
              valueKey="id"
              placeholderLabel="Select"
              onChange={handleChange}
              error={(touched.businessAreaId && !!errors.businessAreaId) || false}
              errorText={errors.businessAreaId}
              data-testid="business-area"
            />

            <Select
              label="Verizon functional area"
              name="functionalAreaId"
              value={functionalAreaId}
              items={functionalAreas}
              optionLabels={["name"]}
              valueKey="id"
              placeholderLabel="Select"
              onChange={handleChange}
              error={(touched.functionalAreaId && !!errors.functionalAreaId) || false}
              errorText={errors.functionalAreaId}
              data-testid="functional-area"
            />
          </TwoUp>

          {/* Row: client name | director name */}
          <TwoUp pd="0">
            <Input
              name="verizonClientName"
              label="Verizon client name"
              tooltipContent="This is the main point of contact at Verizon for the project. For agencies it’s the primary Verizon client. For internal Verizon submissions, the submitter and Verizon client may be the same person."
              value={verizonClientName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={(touched.verizonClientName && !!errors.verizonClientName) || false}
              errorText={errors.verizonClientName}
              required
              data-testid="verizon-client-name"
            />

            <Input
              name="verizonDirectorName"
              label="Verizon director name"
              tooltipContent="The director level of the primary Verizon client or contact."
              value={verizonDirectorName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={(touched.verizonDirectorName && !!errors.verizonDirectorName) || false}
              errorText={errors.verizonDirectorName}
              required
              data-testid="verizon-director-name"
            />
          </TwoUp>

          {/* Row: VP name | (empty) */}
          <TwoUp pd="0">
            <Input
              name="verizonVpName"
              label="Verizon VP name"
              tooltipContent="The VP level the Verizon director reports into. Executive Director is acceptable but please avoid EVP or SVP titles."
              value={verizonVpName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={(touched.verizonVpName && !!errors.verizonVpName) || false}
              errorText={errors.verizonVpName}
              required
              data-testid="verizon-vp-name"
            />
            <div />
          </TwoUp>
        </StyledSection>

        {/* ─── Attach your file(s) ──────────────────────────────────── */}
        <StyledSection gap="1.25rem">
          <Title size="large" bold>
            Attach your file(s)
          </Title>

          {/* Submission checklist */}
          <FlexBox gap="0.25rem">
            <Body size="large" bold>
              Submission Checklist:
            </Body>
            <ChecklistWrapper>
              <MdToHtml
                pMargin="0"
                pSize="large"
                content={`- Submit flat, non-editable files.
- Combine related assets into a single PDF.
- Keep file size under 250 MB.
- Submit only approved formats: PDF, JPG, PNG, TIFF, MP4, MOV.`}
              />
            </ChecklistWrapper>
          </FlexBox>

          <UploadHelpGroup>
            <BlackLinkWrapper>
              <Body size="large">
                Having issues uploading your files? Email us at <a href="mailto:brandcompliance@verizon.com">brandcompliance@verizon.com</a>.
              </Body>
            </BlackLinkWrapper>

            <DragWrapper>
              <DragAndDrop
                files={files}
                fileSizeLimitInMb={250}
                onChange={(newFiles: File[]) => handleFilesChange(newFiles)}
                onDelete={(file: File) => handleRemoveFile(file)}
                uploadingFiles={uploadingFiles}
                hideFileList
                hideFileSizeLimitText
                promptText="Drag and drop your file(s) or"
              />
            </DragWrapper>
          </UploadHelpGroup>

          {/* File rows live outside the dashed drop zone, per design */}
          {files.length > 0 && (
            <FlexBox gap="1rem">
              {files.map((file: File) => (
                <BrandComplianceFileRow
                  key={file.name}
                  file={file}
                  progressEvent={uploadProgress[file.name]}
                  uploading={uploadingFiles.includes(file.name)}
                  deleting={false}
                  onDelete={handleRemoveFile}
                  assetType={fileAssetTypes[file.name] ?? ""}
                  assetTypes={assetTypes}
                  onAssetTypeChange={(value: string) => handleAssetTypeChange(file, value)}
                  error={!!touched.fileAssetTypes && !fileAssetTypes[file.name]}
                  errorText="You must select an asset type"
                />
              ))}
            </FlexBox>
          )}
          {touched.files && !!errors.files && (
            <Body size="small" color="#ee0000">
              {errors.files as string}
            </Body>
          )}

          <ButtonGroup align="flex-start" pd="0.5rem 0">
            <Button
              type="submit"
              use="primary"
              disabled={
                submitLoading ||
                uploadLoading ||
                uploadingFiles.length > 0 ||
                !(dirty && isValid)
              }
              data-testid="submit"
            >
              Submit
            </Button>
          </ButtonGroup>
        </StyledSection>
      </StyledForm>

      {(submitLoading && !showSuccess) && (
        <Loader active={submitLoading} />
      )}
    </>
  );
};

export default BrandComplianceForm;
